import React from "react";
import { useTranslation } from "react-i18next";
import { APP_LANGUAGES, resolveAppLanguage } from "../i18n";

const SITE = "https://eudora.ytitov.com";
const OG_IMAGE = `${SITE}/og-eudora.png`;

function eudrHrefFor(lang: string): string {
  const code = (lang || "en").split("-")[0].toLowerCase();
  const eu = new Set([
    "bg", "cs", "da", "de", "el", "en", "es", "et", "fi", "fr", "ga", "hr", "hu",
    "it", "lt", "lv", "mt", "nl", "pl", "pt", "ro", "sk", "sl", "sv",
  ]);
  const pathLang = eu.has(code) ? code : "en";
  return `https://eur-lex.europa.eu/legal-content/${pathLang.toUpperCase()}/TXT/?uri=CELEX%3A32023R1115`;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  const selector = extra?.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (extra) {
      for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const OG_LOCALE: Record<string, string> = {
  en: "en_US",
  de: "de_DE",
  fr: "fr_FR",
  es: "es_ES",
  it: "it_IT",
  pt: "pt_PT",
  nl: "nl_NL",
  pl: "pl_PL",
  ru: "ru_RU",
  uk: "uk_UA",
  ar: "ar_SA",
  zh: "zh_CN",
};

/**
 * Lightweight head manager for the public landing (no react-helmet dependency).
 * US-806: only used on public Welcome — does not touch Keycloak.
 */
export function WelcomeSeo() {
  const { t, i18n } = useTranslation();
  const lang = resolveAppLanguage(i18n.language);
  const title = t("investor.welcome.seo.title");
  const description = t("investor.welcome.seo.description");
  const eudrHref = eudrHrefFor(lang);

  React.useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index,follow,max-image-preview:large");
    upsertLink("canonical", `${SITE}/`);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "EUDoRa");
    upsertMeta("property", "og:url", `${SITE}/`);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:locale", OG_LOCALE[lang] || "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    // hreflang alternatives (SPA serves one URL; language is client-side)
    upsertLink("alternate", `${SITE}/`, { hreflang: "x-default" });
    for (const code of APP_LANGUAGES) {
      const hrefLang = code === "zh" ? "zh-CN" : code;
      upsertLink("alternate", `${SITE}/`, { hreflang: hrefLang });
    }

    upsertJsonLd("eudora-jsonld-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "EUDoRa",
      url: SITE,
      logo: OG_IMAGE,
      description,
    });
    upsertJsonLd("eudora-jsonld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "EUDoRa",
      url: SITE,
      inLanguage: lang === "zh" ? "zh-CN" : lang,
      description,
    });
    upsertJsonLd("eudora-jsonld-software", {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "EUDoRa DDS Passport",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: SITE,
      description,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        description: "Demo access",
      },
      about: {
        "@type": "CreativeWork",
        name: "Regulation (EU) 2023/1115",
        url: eudrHref,
      },
    });
  }, [title, description, lang, eudrHref]);

  return null;
}
